/**
 * CoverageRespond — Deep link target for coverage notifications.
 * Sprint 16: FR-COV-05
 *
 * URL: /coverage/respond?candidate_id=N&request_id=N
 * Shows shift details and accept/decline buttons, or auto-confirms via URL param.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../lib/api';
import AppLayout from '../components/layout/AppLayout';

type ResponseState = 'loading' | 'prompt' | 'submitting' | 'success' | 'error' | 'already_filled';

export default function CoverageRespond() {
  const { user: _user } = useAuth();
  const [params] = useSearchParams();
  const candidateId = params.get('candidate_id');
  const _requestId = params.get('request_id');
  const autoResponse = params.get('response'); // 'accepted' or 'declined'

  const [state, setState] = useState<ResponseState>('loading');
  const [message, setMessage] = useState('');
  const [respondedAs, setRespondedAs] = useState<string | null>(null);
  const [shiftInfo, setShiftInfo] = useState<{
    battalion_name?: string;
    shift_start?: string;
    shift_end?: string;
    shift_type?: string;
    position_needed?: string;
  } | null>(null);

  useEffect(() => {
    if (!candidateId) {
      setState('error');
      setMessage('Missing candidate_id parameter.');
      return;
    }

    // Fetch shift details for display
    apiFetch<Record<string, unknown>>(`/api/coverage/candidate/${candidateId}/info`)
      .then((info) => {
        setShiftInfo({
          battalion_name: info.battalion_name as string,
          shift_start: info.shift_start as string,
          shift_end: info.shift_end as string,
          shift_type: info.shift_type as string,
          position_needed: info.position_needed as string | undefined,
        });
      })
      .catch(() => {
        // Best-effort — page still works without shift details
      });

    if (autoResponse && ['accepted', 'declined'].includes(autoResponse)) {
      submitResponse(autoResponse as 'accepted' | 'declined');
    } else {
      setState('prompt');
    }
  }, [candidateId, autoResponse]);

  const submitResponse = async (response: 'accepted' | 'declined') => {
    setState('submitting');
    try {
      const res = await apiFetch<{
        response?: string;
        message: string;
        shift_already_filled?: boolean;
      }>(`/api/coverage/candidate/${candidateId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ response }),
      });

      setRespondedAs(response);

      if (res.shift_already_filled) {
        setState('already_filled');
        setMessage(res.message);
      } else {
        setState('success');
        setMessage(res.message);
      }
    } catch (err) {
      setState('error');
      setMessage((err as Error).message);
    }
  };

  return (
    <AppLayout>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 16px', textAlign: 'center' }}>
        {state === 'loading' && <div style={{ color: '#999' }}>Loading...</div>}

        {state === 'prompt' && (
          <div>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(212, 168, 67, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 28,
              }}
            >
              🚒
            </div>
            <h2 style={{ color: '#D4A843', fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Coverage Request</h2>
            <p style={{ color: '#999', fontSize: 15, margin: '0 0 16px' }}>
              {shiftInfo?.battalion_name
                ? `${shiftInfo.battalion_name} needs coverage. Can you run duty?`
                : 'Your battalion needs help covering a shift. Can you run duty?'}
            </p>

            {shiftInfo && (
              <div
                style={{
                  textAlign: 'left',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  borderRadius: 10,
                  padding: 16,
                  marginBottom: 24,
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                {shiftInfo.shift_start && (
                  <div style={{ color: '#ccc', fontSize: 14, marginBottom: 6 }}>
                    <span style={{ color: '#888' }}>Date: </span>
                    {new Date(shiftInfo.shift_start).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'America/New_York',
                    })}
                  </div>
                )}
                {shiftInfo.shift_start && shiftInfo.shift_end && (
                  <div style={{ color: '#ccc', fontSize: 14, marginBottom: 6 }}>
                    <span style={{ color: '#888' }}>Time: </span>
                    {new Date(shiftInfo.shift_start).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZone: 'America/New_York',
                    })}
                    {' – '}
                    {new Date(shiftInfo.shift_end).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZone: 'America/New_York',
                    })}
                  </div>
                )}
                {shiftInfo.shift_type && (
                  <div style={{ color: '#ccc', fontSize: 14, marginBottom: 6 }}>
                    <span style={{ color: '#888' }}>Type: </span>
                    {shiftInfo.shift_type.charAt(0).toUpperCase() + shiftInfo.shift_type.slice(1)}
                  </div>
                )}
                {shiftInfo.position_needed && (
                  <div style={{ color: '#ccc', fontSize: 14 }}>
                    <span style={{ color: '#888' }}>Position: </span>
                    {shiftInfo.position_needed.replace(/_/g, ' ')}
                  </div>
                )}
              </div>
            )}

            {!shiftInfo && <div style={{ marginBottom: 24 }} />}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => submitResponse('declined')}
                style={{
                  padding: '14px 28px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 10,
                  color: '#999',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: 120,
                }}
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => submitResponse('accepted')}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#22C55E',
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: 'pointer',
                  minWidth: 120,
                }}
              >
                Accept
              </button>
            </div>
          </div>
        )}

        {state === 'submitting' && <div style={{ color: '#D4A843', fontSize: 16 }}>Submitting your response...</div>}

        {state === 'success' && (
          <div>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: respondedAs === 'accepted' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 28,
              }}
            >
              {respondedAs === 'accepted' ? '✅' : '👋'}
            </div>
            <h2
              style={{
                color: respondedAs === 'accepted' ? '#22C55E' : '#fff',
                fontSize: 22,
                fontWeight: 700,
                margin: '0 0 8px',
              }}
            >
              {respondedAs === 'accepted' ? "You're Confirmed!" : 'Response Recorded'}
            </h2>
            <p style={{ color: '#999', fontSize: 15, margin: 0 }}>{message}</p>
          </div>
        )}

        {state === 'already_filled' && (
          <div>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 28,
              }}
            >
              ℹ️
            </div>
            <h2 style={{ color: '#3B82F6', fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
              Shift Already Covered
            </h2>
            <p style={{ color: '#999', fontSize: 15, margin: 0 }}>{message}</p>
          </div>
        )}

        {state === 'error' && (
          <div>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(200, 50, 43, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: 28,
              }}
            >
              ⚠️
            </div>
            <h2 style={{ color: '#C8322B', fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Error</h2>
            <p style={{ color: '#999', fontSize: 15, margin: 0 }}>{message}</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
